import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { API } from "../App";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { handleImgError } from "../lib/imageFallback";

// Content for popup modals
const POPUP_CONTENT = {
  terms: {
    title: "Términos y Condiciones",
    content: `
      <h3 class="font-semibold mb-2">1. Aceptación de los Términos</h3>
      <p class="mb-4 text-sm text-gray-600">Al usar Clueer, aceptas estos términos y condiciones. Si no estás de acuerdo, no uses la plataforma.</p>
      
      <h3 class="font-semibold mb-2">2. Subastas y Ofertas</h3>
      <p class="mb-4 text-sm text-gray-600">Las ofertas son compromisos de compra. Si ganas una subasta, te comprometes a completar la transacción mediante pago contraentrega.</p>
      
      <h3 class="font-semibold mb-2">3. Pago Contraentrega</h3>
      <p class="mb-4 text-sm text-gray-600">El pago se realiza al momento de recibir el producto. Tienes derecho a revisar el producto antes de pagar.</p>
      
      <h3 class="font-semibold mb-2">4. Cancelaciones</h3>
      <p class="mb-4 text-sm text-gray-600">Las cancelaciones deben realizarse antes de la entrega. Cancelaciones repetidas pueden resultar en suspensión de cuenta.</p>
      
      <h3 class="font-semibold mb-2">5. Responsabilidad</h3>
      <p class="mb-4 text-sm text-gray-600">Clueer actúa como intermediario. Los vendedores son responsables de la calidad de los productos.</p>
    `
  },
  privacy: {
    title: "Política de Privacidad",
    content: `
      <h3 class="font-semibold mb-2">Información que Recopilamos</h3>
      <p class="mb-4 text-sm text-gray-600">Recopilamos tu nombre, email, número de WhatsApp y datos de transacciones para brindarte nuestros servicios.</p>
      
      <h3 class="font-semibold mb-2">Uso de la Información</h3>
      <p class="mb-4 text-sm text-gray-600">Tu información se usa para procesar transacciones, enviarte notificaciones y mejorar nuestros servicios.</p>
      
      <h3 class="font-semibold mb-2">WhatsApp</h3>
      <p class="mb-4 text-sm text-gray-600">Tu número de WhatsApp se usa como método de autenticación y para enviarte actualizaciones sobre tus ofertas y compras.</p>
      
      <h3 class="font-semibold mb-2">Seguridad</h3>
      <p class="mb-4 text-sm text-gray-600">Implementamos medidas de seguridad para proteger tu información personal.</p>
    `
  },
  faq: {
    title: "Preguntas Frecuentes",
    content: `
      <h3 class="font-semibold mb-2">¿Cómo funciona el pago contraentrega?</h3>
      <p class="mb-4 text-sm text-gray-600">Recibes el producto, lo revisas y si todo está bien, realizas el pago. ¡Sin riesgos!</p>
      
      <h3 class="font-semibold mb-2">¿Cómo participo en una subasta?</h3>
      <p class="mb-4 text-sm text-gray-600">Regístrate con tu WhatsApp, encuentra un producto que te guste y haz tu oferta. Tu número de WhatsApp es tu contraseña.</p>
      
      <h3 class="font-semibold mb-2">¿Qué pasa si gano una subasta?</h3>
      <p class="mb-4 text-sm text-gray-600">Te notificaremos por WhatsApp y coordinaremos la entrega. Pagas solo cuando recibas el producto.</p>
      
      <h3 class="font-semibold mb-2">¿Puedo cancelar una oferta?</h3>
      <p class="mb-4 text-sm text-gray-600">Puedes cancelar antes de que termine la subasta. Después de ganar, contacta al vendedor para cancelar.</p>
      
      <h3 class="font-semibold mb-2">¿Hay costo de envío?</h3>
      <p class="mb-4 text-sm text-gray-600">El costo de envío se indica en cada producto. Algunos productos incluyen envío gratis.</p>
    `
  },
  contact: {
    title: "Contacto",
    content: `
      <div class="text-center">
        <h3 class="font-semibold mb-4">¿Tienes preguntas?</h3>
        <p class="mb-6 text-sm text-gray-600">Estamos aquí para ayudarte</p>
        
        <div class="space-y-4">
          <a href="https://wa.me/50760000000" target="_blank" class="flex items-center justify-center gap-2 bg-green-500 text-white py-3 px-6 rounded-full hover:bg-green-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
          
          <a href="mailto:info@ibiid.com" class="flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 px-6 rounded-full hover:bg-[#1E293B] transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            info@ibiid.com
          </a>
        </div>
      </div>
    `
  },
  how_to_buy: {
    title: "¿Cómo Comprar?",
    content: `
      <div class="space-y-6">
        <div class="flex gap-4">
          <div class="w-8 h-8 rounded-full bg-[#F97316] text-white flex items-center justify-center flex-shrink-0 font-bold">1</div>
          <div>
            <h4 class="font-semibold">Regístrate con WhatsApp</h4>
            <p class="text-sm text-gray-600">Tu número de WhatsApp (8 dígitos) es tu contraseña. ¡Así de fácil!</p>
          </div>
        </div>
        
        <div class="flex gap-4">
          <div class="w-8 h-8 rounded-full bg-[#F97316] text-white flex items-center justify-center flex-shrink-0 font-bold">2</div>
          <div>
            <h4 class="font-semibold">Encuentra tu Producto</h4>
            <p class="text-sm text-gray-600">Explora subastas activas o productos de venta directa. Usa el buscador para encontrar lo que necesitas.</p>
          </div>
        </div>
        
        <div class="flex gap-4">
          <div class="w-8 h-8 rounded-full bg-[#F97316] text-white flex items-center justify-center flex-shrink-0 font-bold">3</div>
          <div>
            <h4 class="font-semibold">Haz tu Oferta o Reserva</h4>
            <p class="text-sm text-gray-600">En subastas, oferta el precio que consideres justo. En venta directa, reserva al precio fijo.</p>
          </div>
        </div>
        
        <div class="flex gap-4">
          <div class="w-8 h-8 rounded-full bg-[#F97316] text-white flex items-center justify-center flex-shrink-0 font-bold">4</div>
          <div>
            <h4 class="font-semibold">Recibe y Paga</h4>
            <p class="text-sm text-gray-600">Coordinamos la entrega. Revisas el producto y si todo está bien, pagas. ¡Sin riesgos!</p>
          </div>
        </div>
        
        <div class="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <p class="text-sm text-green-800 font-medium text-center">
            ✨ Deja una reseña después de tu compra y gana mínimo $2 de cashback vía Yappy
          </p>
        </div>
      </div>
    `
  }
};

const BannerCarousel = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    // Auto-slide every 4 seconds
    if (banners.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 4000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [banners.length]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners`);
      setBanners(response.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    // Reset the auto-slide timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
    }
  };

  const goToPrev = () => {
    goToSlide(currentIndex === 0 ? banners.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    goToSlide((currentIndex + 1) % banners.length);
  };

  if (loading) {
    return (
      <div className="w-full aspect-[3/1] bg-gradient-to-r from-[#F1F5F9] to-[#E2E8F0] rounded-2xl animate-pulse mx-4" />
    );
  }

  if (banners.length === 0) {
    return null;
  }

  // Handle banner action click
  const handleBannerAction = (banner) => {
    const actionType = banner.action_type || "none";
    const actionValue = banner.action_value;

    switch (actionType) {
      case "popup":
        if (actionValue && POPUP_CONTENT[actionValue]) {
          setPopupContent(POPUP_CONTENT[actionValue]);
          setPopupOpen(true);
        }
        break;
      case "navigate":
        if (actionValue) {
          navigate(actionValue);
        }
        break;
      case "product":
        if (actionValue) {
          navigate(`/product/${actionValue}`);
        }
        break;
      case "external_url":
        if (actionValue) {
          window.open(actionValue, "_blank", "noopener,noreferrer");
        }
        break;
      default:
        // Legacy support: if link_url exists, use it
        if (banner.link_url) {
          if (banner.link_url.startsWith("http")) {
            window.open(banner.link_url, "_blank", "noopener,noreferrer");
          } else {
            navigate(banner.link_url);
          }
        }
        break;
    }
  };

  return (
    <>
      <div className="relative px-4 mb-4" data-testid="banner-carousel">
        {/* Main carousel container */}
        <div className="relative overflow-hidden rounded-2xl shadow-lg">
          {/* Slides */}
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {banners.map((banner) => (
              <div
                key={banner.banner_id}
                className="w-full flex-shrink-0 relative"
              >
                <BannerSlide 
                  banner={banner} 
                  onActionClick={() => handleBannerAction(banner)}
                />
              </div>
            ))}
          </div>

          {/* Navigation arrows - more translucent */}
          {banners.length > 1 && (
            <>
              <button
                onClick={goToPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/30 transition-colors"
                data-testid="carousel-prev"
              >
                <ChevronLeft size={18} className="text-white/80" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/30 transition-colors"
                data-testid="carousel-next"
              >
                <ChevronRight size={18} className="text-white/80" />
              </button>
            </>
          )}
        </div>

        {/* Dots indicator */}
        {banners.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "w-6 bg-[#F97316]"
                    : "w-1.5 bg-[#CBD5E1] hover:bg-[#94A3B8]"
                }`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Popup Modal for Terms, FAQ, Contact, etc. */}
      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{popupContent?.title}</DialogTitle>
          </DialogHeader>
          <div 
            className="py-4"
            dangerouslySetInnerHTML={{ __html: popupContent?.content || "" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Individual banner slide component
const BannerSlide = ({ banner, onActionClick }) => {
  const hasAction = banner.action_type && banner.action_type !== "none" || banner.link_url;
  const hasCaption = !!(banner.title || banner.subtitle || banner.button_text);

  return (
    <div
      className={`relative aspect-[3/1] overflow-hidden bg-[#F1F5F9] ${hasAction ? 'cursor-pointer' : ''}`}
      onClick={hasAction ? onActionClick : undefined}
    >
      {/* Banner image - using <img> instead of CSS background to avoid Chrome mobile GPU rasterization bug */}
      <img
        src={banner.image_url}
        alt={banner.title || "banner"}
        loading="eager"
        decoding="async"
        onError={handleImgError}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Gradient overlay + caption only rendered when there's actual text/CTA, so a banner with no title stays clean */}
      {hasCaption && (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-10 right-10 p-3 sm:p-4">
            {banner.title && (
              <h3 className="text-white text-sm sm:text-lg font-bold drop-shadow-lg mb-0.5">
                {banner.title}
              </h3>
            )}
            {banner.subtitle && (
              <p className="text-white/90 text-[10px] sm:text-sm drop-shadow-md mb-1.5">
                {banner.subtitle}
              </p>
            )}
            {banner.button_text && (
              <button
                className="px-3 py-1 bg-[#F97316] hover:bg-[#EA580C] text-white text-[10px] sm:text-xs font-semibold rounded-full shadow-lg transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick();
                }}
              >
                {banner.button_text}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BannerCarousel;
